/**
 * Wording for the review of individual AI requests (quality control): what happened
 * with a request, in the same terms as the usage overview.
 */

/** Filter options for "what did the worker do with it" (values as the API expects). */
export const REQUEST_OUTCOME_FILTERS = [
  { value: null, title: "Alles" },
  { value: "accepted", title: "Iets overgenomen" },
  { value: "not_accepted", title: "Niets overgenomen" },
  { value: "thumbs_up", title: "Duim omhoog" },
  { value: "thumbs_down", title: "Duim omlaag" },
  { value: "failed", title: "Mislukt" },
];

/**
 * One-line outcome of a request with a colour for its chip.
 *
 * @param {Object} log - a row from /api/v1/ai_usage_logs
 * @returns {{ label: string, color: string|undefined }}
 */
export function requestOutcome(log) {
  if (log.status === "error") return { label: "Mislukt", color: "error" };
  if (log.status === "invalid_output")
    return { label: "Onbruikbaar antwoord", color: "error" };

  const suggested = log.suggested_count;
  const accepted = log.accepted_count || 0;
  // Skills without countable suggestions (e.g. a summary) have no count at all.
  if (suggested === null || suggested === undefined)
    return { label: "Gemaakt", color: undefined };
  if (suggested === 0) return { label: "Geen voorstellen", color: undefined };
  if (accepted === 0) return { label: "Niets overgenomen", color: "warning" };
  return {
    label:
      suggested === 1
        ? "Overgenomen"
        : `${accepted} van ${suggested} overgenomen`,
    color: "success",
  };
}

/**
 * Whether one suggestion ended up on the saved report.
 *
 * @param {string[]|null|undefined} savedKeys - keys on the saved report, or null when
 *   that cannot be told (report never saved, or the skill does not write to a report)
 * @param {string|number} key - the suggestion's key
 * @returns {boolean|null} null = unknown
 */
export function suggestionSaved(savedKeys, key) {
  if (!Array.isArray(savedKeys)) return null;
  return savedKeys.includes(String(key));
}

/**
 * Why a text check comes without a rewrite to take over. Empty when there is a
 * rewrite, and for results from before this was recorded.
 *
 * @param {string|undefined} status - "proposed" | "none" | "unchanged" | "not_a_rewrite"
 * @param {{ forReviewer?: boolean }} [options] - wording for quality control
 * @returns {string}
 */
export function rewriteStatusNote(status, { forReviewer = false } = {}) {
  switch (status) {
    case "none":
    case "unchanged":
      return forReviewer
        ? "De AI stelde geen andere tekst voor."
        : "De AI stelt geen andere tekst voor.";
    case "not_a_rewrite":
      return forReviewer
        ? "Het tekstvoorstel van de AI is niet aan de medewerker getoond: het kwam grotendeels niet uit de eigen tekst van de medewerker (een opmerking over het rapport, of tekst die er niet stond). Wat de AI teruggaf staat onder 'Ruw antwoord van de AI'."
        : "Er is geen tekstvoorstel: wat de AI teruggaf was geen verbeterde versie van je eigen tekst.";
    default:
      return "";
  }
}

/**
 * How a source reference of a summary held up when the server checked the words the
 * AI quoted against the report. Null for summaries made before references were checked.
 *
 * @param {string|undefined} reference - "confirmed" | "corrected" | "unconfirmed"
 * @returns {{ label: string, color: string|undefined }|null}
 */
export function briefingReference(reference) {
  switch (reference) {
    case "confirmed":
      return { label: "Bron klopt", color: "success" };
    case "corrected":
      return { label: "Bron gecorrigeerd", color: "warning" };
    case "unconfirmed":
      return { label: "Bron niet gevonden", color: "error" };
    default:
      return null;
  }
}

/**
 * One sentence on the references of a summary request, from the numbers in its log.
 * @param {{ key_points: number, confirmed: number, corrected: number, unconfirmed: number }|undefined} references
 * @returns {string} "" when the request was made before references were checked
 */
export function referencesSummary(references) {
  if (!references) return "";
  if (!references.key_points) return "De samenvatting had geen losse punten.";
  const parts = [`${references.confirmed} klopten`];
  if (references.corrected)
    parts.push(
      `${references.corrected} verwezen naar het verkeerde rapport en ${references.corrected === 1 ? "is" : "zijn"} gecorrigeerd`,
    );
  if (references.unconfirmed)
    parts.push(
      `bij ${references.unconfirmed} stonden de aangehaalde woorden in geen enkel rapport`,
    );
  return `Bronnen van de ${references.key_points} punten: ${parts.join(", ")}.`;
}

/** "10 okt 2026 16:38" in Dutch; "" without a value. */
export function formatDateTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
